import { useState, useEffect, useRef } from "react";
import heroImage from './images/hero-image.jpg';
import csLogo from './images/cs-logo-white.png';


const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const useWindowWidth = () => {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(32px)",
      transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`
    }}>
      {children}
    </div>
  );
};

// Dark Blue Palette:
// Deep background:   #010048 / #010057 (near-black blue)
// Section alt:       #000080 (Deep Navy) / #003366 (Midnight Blue)
// Cards/surfaces:    #003153 (Prussian Blue) / #044e84 (Modern Navy)
// Accent/CTA:        #7ad6f4 (light sky blue) / #1e90ff (bright blue)
// Gold highlight:    #FFD700
// Text primary:      #FFFFFF
// Text secondary:    #E0E0E0
// Text muted:        #8aaec8
// Borders:           #005b96 / #044e84

export default function CadenceSolutions() {
  const [, setScrollY] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const width = useWindowWidth();
  const isMobile = width < 768;

  const sectionPadding = 48;
  const heroImageBottom = 20;  // vh units — increase to push further down

  const services = [
    { icon: "⚙️", title: "Process Automation", desc: "Eliminate repetitive manual tasks with intelligent workflow automation that adapts to your business needs." },
    { icon: "📊", title: "Data-Driven Insights", desc: "Transform raw data into actionable intelligence with automated reporting and analytics dashboards." },
    { icon: "🔗", title: "Seamless Integration", desc: "Connect your existing tools and systems into a unified, frictionless ecosystem without disruption." },
    { icon: "🤖", title: "AI-Powered Solutions", desc: "Leverage emerging AI technologies to unlock new levels of productivity and decision-making capability." },
    { icon: "📈", title: "Scalable Architecture", desc: "Solutions designed to grow with your business — from small teams to enterprise operations." },
    { icon: "🛡️", title: "Reliable & Secure", desc: "Production-grade automation built with security, uptime, and compliance at the forefront." },
  ];

  const values = [
    { label: "Innovation", detail: "Pushing boundaries of what automation can achieve" },
    { label: "Reliability", detail: "Dependable systems your business can count on" },
    { label: "Partnership", detail: "Your success is the measure of our own" },
    { label: "Accessibility", detail: "Enterprise power at small business pricing" },
  ];

  return (
    <div style={{ fontFamily: "'Georgia', 'Times New Roman', serif", background: "#010048", color: "#FFFFFF", margin: 0, padding: 0, overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Barlow:wght@300;400;500&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; background: #010048; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #000080; }
        ::-webkit-scrollbar-thumb { background: #7ad6f4; border-radius: 2px; }
        .cormorant { font-family: 'Cormorant Garamond', Georgia, serif; }
        .barlow { font-family: 'Barlow', sans-serif; }
        .nav-link {
          font-family: 'Barlow', sans-serif;
          font-size: 16px;
          font-weight: 400;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #e8edf5;
          text-decoration: none;
          transition: color 0.3s;
          cursor: pointer;
          background: none; border: none; padding: 0;
        }
        .nav-link:hover { color: #7ad6f4; }
        .btn-primary {
          display: inline-block;
          background: transparent;
          color: #7ad6f4;
          font-family: 'Barlow', sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 3px;
          text-transform: uppercase;
          padding: 15px 40px;
          border: 1px solid #7ad6f4;
          cursor: pointer;
          transition: background 0.3s, color 0.3s;
          text-decoration: none;
        }
        .btn-primary:hover { background: #7ad6f4; color: #010048; }
        .btn-outline {
          display: inline-block;
          background: transparent;
          color: #7ad6f4;
          font-family: 'Barlow', sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 3px;
          text-transform: uppercase;
          padding: 15px 40px;
          border: 1px solid #7ad6f4;
          cursor: pointer;
          transition: background 0.3s, color 0.3s;
          text-decoration: none;
        }
        .btn-outline:hover { background: #7ad6f4; color: #010048; }
        .service-card {
          background: #ffffff;
          border: 1px solid #dce8f5;
          padding: 40px 32px;
          transition: border-color 0.3s, transform 0.3s, box-shadow 0.3s;
        }
        .service-card:hover { border-color: #003366; transform: translateY(-4px); box-shadow: 0 12px 40px rgba(0,51,102,0.12); }
        .divider { width: 60px; height: 2px; background: #FFD700; }
        .accent { color: #7ad6f4; }
        .gold { color: #FFD700; }
        .muted { color: #8aaec8; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 0.4; } }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>

      {/* NAV */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: "rgba(1,0,72,0.98)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid #003366",
        transition: "all 0.4s ease",
        padding: isMobile ? "0 24px" : `0 ${sectionPadding}px`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: "72px",
        backgroundImage: "linear-gradient(rgba(200,169,110,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(200,169,110,0.04) 1px, transparent 1px)",
        backgroundSize: "80px 80px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <img src={csLogo} alt="Cadence Solutions Logo" style={{ height: isMobile ? "36px" : "48px", width: "auto" }} />
          <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
            <span className="cormorant" style={{ fontSize: isMobile ? "22px" : "28px", fontWeight: 600, color: "#FFFFFF", letterSpacing: "1px" }}>Cadence</span>
            <span className="barlow" style={{ fontSize: isMobile ? "12px" : "20px", letterSpacing: "3px", color: "#7ad6f4", textTransform: "uppercase" }}>Solutions</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "40px", alignItems: "center" }}>
          {!isMobile && ["Mission", "Services", "Vision", "Contact"].map(s => (
            <button key={s} className="nav-link" onClick={() => document.getElementById(s.toLowerCase())?.scrollIntoView({ behavior: "smooth" })}>{s}</button>
          ))}
          {!isMobile && (
            <a href="mailto:info@cadencesolutionsllc.com" className="btn-primary" style={{ padding: "10px 24px", fontSize: "16px" }}>Get Started</a>
          )}
          {isMobile && (
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                style={{
                  background: "transparent",
                  border: "1px solid #7ad6f4",
                  color: "#7ad6f4",
                  padding: "8px 14px",
                  cursor: "pointer",
                  fontSize: "20px",
                  lineHeight: 1,
                  borderRadius: "2px"
                }}>
                {menuOpen ? "✕" : "☰"}
              </button>
              {menuOpen && (
                <div style={{
                  position: "absolute",
                  top: "48px",
                  right: 0,
                  background: "rgba(1,0,72,0.98)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid #003366",
                  minWidth: "200px",
                  zIndex: 200
                }}>
                  {["Mission", "Services", "Vision", "Contact"].map(s => (
                    <button
                      key={s}
                      className="nav-link"
                      onClick={() => {
                        document.getElementById(s.toLowerCase())?.scrollIntoView({ behavior: "smooth" });
                        setMenuOpen(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "16px 24px",
                        borderBottom: "1px solid #003366",
                        fontSize: "13px"
                      }}>
                      {s}
                    </button>
                  ))}
                    <a href="mailto:info@cadencesolutionsllc.com"
                    className="btn-primary"
                    onClick={() => setMenuOpen(false)}
                    style={{
                      display: "block",
                      margin: "16px",
                      textAlign: "center",
                      fontSize: "12px"
                    }}>
                    Get Started
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* HERO */}
      <section style={{
        minHeight: isMobile ? "auto" : "80vh", display: "flex", flexDirection: "column", justifyContent: "center",
        position: "relative", padding: isMobile ? "100px 24px 48px" : `120px ${sectionPadding}px 80px`, overflow: isMobile ? "visible" : "hidden",
        background: "linear-gradient(135deg, #010048 0%, #000080 55%, #003366 100%)"
      }}>
        {/* Subtle grid */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: "linear-gradient(rgba(122,214,244,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(122,214,244,0.05) 1px, transparent 1px)",
          backgroundSize: "80px 80px"
        }} />
        {/* Glow orbs */}
        <div style={{
          position: "absolute", top: "20%", right: "15%", width: "600px", height: "600px",
          background: "radial-gradient(circle, rgba(30,144,255,0.18) 0%, transparent 70%)",
          zIndex: 0, borderRadius: "50%"
        }} />
        <div style={{
          position: "absolute", bottom: "15%", left: "5%", width: "350px", height: "350px",
          background: "radial-gradient(circle, rgba(122,214,244,0.12) 0%, transparent 70%)",
          zIndex: 0, borderRadius: "50%"
        }} />
        {/* Gold accent line top */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, transparent, #FFD700, transparent)", zIndex: 2 }} />

        {/* Hero content + bleed image layout */}
        <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: "80px", maxWidth: "1200px" }}>

        {/* Left side — text content */}
        <div style={{ flex: isMobile ? "0 0 100%" : "0 0 80%", position: "relative", zIndex: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "32px", opacity: 0, animation: "fadeUp 1s 0.2s forwards" }}>
            <div className="divider" />
            <span className="barlow muted" style={{ fontSize: "12px", letterSpacing: "4px", textTransform: "uppercase" }}>Software Automation & AI Solutions</span>
          </div>

          <h1 className="cormorant" style={{
            fontSize: "clamp(32px, 5vw, 96px)", fontWeight: 300, lineHeight: 1.05,
            margin: "0 0 32px", letterSpacing: "-1px",
            opacity: 0, animation: "fadeUp 1s 0.4s forwards"
          }}>
            Automation that drives transformation
          </h1>

          <p className="barlow" style={{
            fontSize: isMobile ? "15px" : "17px", fontWeight: 300, lineHeight: 1.8, color: "#a09880", margin: "0 0 48px",
            opacity: 0, animation: "fadeUp 1s 0.6s forwards"
          }}>
            Cadence Solutions empowers small and mid-sized businesses with intelligent automation — reducing costs, unlocking insights, and freeing your team to focus on higher-value tasks.
          </p>

          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "16px", opacity: 0, animation: "fadeUp 1s 0.8s forwards", alignItems: isMobile ? "center" : "flex-start" }}>
            <a href="mailto:info@cadencesolutionsllc.com" className="btn-primary">Start Your Journey</a>
            <button className="btn-outline" onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}>Our Services</button>
          </div>
        </div>

        {/* Right side — full bleed image with left fade — hidden on mobile */}
        {!isMobile && (
        <div style={{
          position: "absolute",
          top: "-120px",
          right: `calc(-100vw + 100% - ${sectionPadding}px)`,
          bottom: `-${heroImageBottom}vh`,
          left: `-${sectionPadding}px`,
          overflow: "hidden",
          zIndex: 1,
          opacity: 0,
          animation: "fadeUp 1s 0.5s forwards"
        }}>
          <img
            src={heroImage}
            alt="Automation solutions"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
              opacity: 0.3
            }}
          />
          {/* Left alpha fade — bleeds behind text */}
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "70%",
            height: "100%",
            background: "linear-gradient(to right, rgba(1,0,72,1) 0%, rgba(1,0,72,1) 30%, rgba(1,0,72,0.9) 45%, rgba(1,0,72,0.6) 60%, rgba(1,0,72,0.2) 80%, transparent 100%)"
          }} />
          {/* Top fade */}
          <div style={{
            position: "absolute",
            top: 0, left: 0, right: 0,
            height: "180px",
            background: "linear-gradient(to bottom, rgba(1,0,72,1) 0%, transparent 100%)"
          }} />
          {/* Bottom fade */}
          <div style={{
            position: "absolute",
            bottom: 0, left: 0, right: 0,
            height: "120px",
            background: "linear-gradient(to top, rgba(1,0,72,1) 0%, transparent 100%)"
          }} />
        </div>
        )}

      </div>

        {!isMobile && (
        <div className="barlow muted" style={{
          position: "absolute", right: "48px", top: "50%", transform: "translateY(-50%) rotate(90deg)",
          fontSize: "16px", letterSpacing: "5px", textTransform: "uppercase",
          opacity: 0, animation: "fadeIn 1s 1.2s forwards", zIndex: 3, color: "#c8dff0"
        }}>
          cadencesolutionsllc.com
        </div>
        )}

        {isMobile ? (
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: "32px",
            marginTop: "48px",
            position: "relative",
            zIndex: 3
          }}>
            {[["Efficiency", "First"], ["AI", "Powered"], ["Small Biz", "Focused"]].map(([a, b], i) => (
              <div key={i} style={{ opacity: 0, animation: `fadeUp 0.8s ${1 + i * 0.15}s forwards`, textAlign: "center" }}>
                <div className="barlow accent" style={{ fontSize: "12px", fontWeight: 500, letterSpacing: "2px" }}>{a}</div>
                <div className="barlow muted" style={{ fontSize: "12px", letterSpacing: "2px" }}>{b}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            position: "absolute",
            bottom: "48px",
            left: "48px",
            display: "flex",
            gap: "120px",
            zIndex: 3
          }}>
            {[["Efficiency", "First"], ["AI", "Powered"], ["Small Biz", "Focused"]].map(([a, b], i) => (
              <div key={i} style={{ opacity: 0, animation: `fadeUp 0.8s ${1 + i * 0.15}s forwards` }}>
                <div className="barlow accent" style={{ fontSize: "16px", fontWeight: 500, letterSpacing: "2px" }}>{a}</div>
                <div className="barlow muted" style={{ fontSize: "16px", letterSpacing: "2px" }}>{b}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* MISSION — white section */}
      <section id="mission" style={{ padding: isMobile ? "80px 24px" : "120px 48px", borderTop: "4px solid #FFD700", background: "#ffffff" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr", gap: isMobile ? "40px" : "80px", alignItems: "start" }}>
          <Reveal>
            <div className="barlow" style={{ fontSize: "24px", letterSpacing: "4px", textTransform: "uppercase", marginBottom: "16px", color: "#005b96" }}>Our Mission</div>
            <div className="divider" style={{ marginBottom: "32px" }} />
            <h2 className="cormorant" style={{ fontSize: "48px", fontWeight: 300, lineHeight: 1.2, margin: 0, color: "#010048" }}>
              Why we<br /><span style={{ fontStyle: "italic", color: "#003366" }}>exist.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="barlow" style={{ fontSize: "18px", fontWeight: 300, lineHeight: 1.9, color: "#1a1a2e", margin: "0 0 32px" }}>
              Our mission is to empower businesses through innovative software automation solutions that drive efficiency, improve quality, and enable data-driven decisions.
            </p>
            <p className="barlow" style={{ fontSize: "16px", fontWeight: 300, lineHeight: 1.9, color: "#444466" }}>
              We are committed to delivering reliable, scalable, and user-friendly automation tools that streamline processes, reduce operational costs, integrate with your current environment, and enable you to focus on your core business. By fostering a culture of continuous improvement and customer-centricity, we strive to be a trusted partner for organizations seeking to unlock the full potential of digital transformation and AI emerging technologies.
            </p>
          </Reveal>
        </div>
      </section>

      {/* VALUES STRIP — soft off-white */}
      <section style={{ background: "#f0f6fc", borderTop: "1px solid #c8dff0", borderBottom: "1px solid #c8dff0", padding: isMobile ? "48px 24px" : "64px 48px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: "0" }}>
          {values.map((v, i) => (
            <Reveal key={v.label} delay={i * 0.1}>
              <div style={{
                padding: isMobile ? "24px 16px" : "32px",
                borderRight: isMobile ? (i % 2 === 0 ? "1px solid #c8dff0" : "none") : (i < 3 ? "1px solid #c8dff0" : "none"),
                borderBottom: isMobile && i < 2 ? "1px solid #c8dff0" : "none",
                textAlign: "center"
              }}>
                <div className="cormorant" style={{ fontSize: "28px", fontWeight: 400, marginBottom: "12px", color: "#003366" }}>{v.label}</div>
                <div className="barlow" style={{ fontSize: "13px", fontWeight: 300, color: "#445577", lineHeight: 1.6, letterSpacing: "0.5px" }}>{v.detail}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES — white section */}
      <section id="services" style={{ padding: isMobile ? "80px 24px" : "120px 48px", borderTop: "1px solid #dce8f5", background: "#ffffff" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: "80px" }}>
              <div className="barlow" style={{ fontSize: "11px", letterSpacing: "4px", textTransform: "uppercase", marginBottom: "20px", color: "#005b96" }}>What We Offer</div>
              <h2 className="cormorant" style={{ fontSize: "clamp(40px, 5vw, 64px)", fontWeight: 300, lineHeight: 1.15, margin: "0 0 24px", color: "#010048" }}>
                Solutions built for<br /><span style={{ fontStyle: "italic", color: "#003366" }}>real businesses.</span>
              </h2>
              <div className="divider" style={{ margin: "0 auto" }} />
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "1px", background: "#dce8f5" }}>
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="service-card" style={{ height: "100%" }}>
                  <div style={{ fontSize: "32px", marginBottom: "20px" }}>{s.icon}</div>
                  <h3 className="cormorant" style={{ fontSize: "26px", fontWeight: 400, margin: "0 0 16px", color: "#010048" }}>{s.title}</h3>
                  <p className="barlow" style={{ fontSize: "14px", fontWeight: 300, color: "#445577", lineHeight: 1.8, margin: 0 }}>{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VISION */}
      <section id="vision" style={{ padding: isMobile ? "80px 24px" : "120px 48px", background: "#000080", borderTop: "1px solid #005b96" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: isMobile ? "48px" : "80px", alignItems: "start" }}>
            <Reveal>
              <div className="barlow muted" style={{ fontSize: "11px", letterSpacing: "4px", textTransform: "uppercase", marginBottom: "16px" }}>Our Vision</div>
              <div className="divider" style={{ marginBottom: "40px" }} />
              <blockquote className="cormorant" style={{
                fontSize: "clamp(28px, 3.5vw, 42px)", fontWeight: 300, lineHeight: 1.4,
                margin: "0 0 40px", fontStyle: "italic", color: "#FFFFFF"
              }}>
                "Shape the future of small businesses by pioneering affordable intelligent automation."
              </blockquote>
              <p className="barlow" style={{ fontSize: "15px", fontWeight: 300, lineHeight: 1.9, color: "#E0E0E0" }}>
                We aspire to be recognized as a leader in innovative software automation, inspiring transformation and creating lasting value for our clients, employees, and communities. Our vision is a world where every business — regardless of size — can harness the full power of automation and AI.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div style={{ padding: "48px", border: "1px solid #dce8f5", background: "#ffffff", boxShadow: "0 8px 40px rgba(0,0,0,0.25)" }}>
                <div className="cormorant" style={{ fontSize: "56px", fontWeight: 300, lineHeight: 1, marginBottom: "8px", color: "#003366" }}>2025</div>
                <div className="barlow" style={{ fontSize: "11px", letterSpacing: "3px", textTransform: "uppercase", marginBottom: "32px", color: "#005b96" }}>Founded</div>
                <div className="divider" style={{ marginBottom: "32px" }} />
                <div style={{ marginBottom: "24px" }}>
                  <div className="cormorant" style={{ fontSize: "20px", marginBottom: "4px", color: "#010048" }}>Small Business Focus</div>
                  <div className="barlow" style={{ fontSize: "12px", letterSpacing: "1px", color: "#445577" }}>Affordable enterprise-grade tools</div>
                </div>
                <div style={{ marginBottom: "24px" }}>
                  <div className="cormorant" style={{ fontSize: "20px", marginBottom: "4px", color: "#010048" }}>Emerging AI Integration</div>
                  <div className="barlow" style={{ fontSize: "12px", letterSpacing: "1px", color: "#445577" }}>Future-proof your operations</div>
                </div>
                <div>
                  <div className="cormorant" style={{ fontSize: "20px", marginBottom: "4px", color: "#010048" }}>Continuous Improvement</div>
                  <div className="barlow" style={{ fontSize: "12px", letterSpacing: "1px", color: "#445577" }}>We grow as you grow</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" style={{
        padding: isMobile ? "80px 24px" : "120px 48px", textAlign: "center",
        borderTop: "1px solid #005b96", position: "relative", overflow: "hidden",
        background: "linear-gradient(135deg, #010048 0%, #003366 50%, #000080 100%)"
      }}>
        {/* Gold top accent */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: "linear-gradient(90deg, transparent, #FFD700 40%, #FFD700 60%, transparent)" }} />
        <div style={{
          position: "absolute", top: "50%", left: "50%", width: "900px", height: "500px",
          background: "radial-gradient(ellipse, rgba(30,144,255,0.15) 0%, transparent 70%)",
          transform: "translate(-50%,-50%)", zIndex: 0
        }} />
        <Reveal>
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="barlow muted" style={{ fontSize: "11px", letterSpacing: "4px", textTransform: "uppercase", marginBottom: "24px" }}>Ready to Begin?</div>
            <h2 className="cormorant" style={{ fontSize: "clamp(40px, 6vw, 72px)", fontWeight: 300, lineHeight: 1.15, margin: "0 0 24px", color: "#FFFFFF" }}>
              Let's build something<br /><span style={{ fontStyle: "italic", color: "#7ad6f4" }}>extraordinary together.</span>
            </h2>
            <div className="divider" style={{ margin: "0 auto 40px" }} />
            <p className="barlow" style={{ fontSize: "16px", fontWeight: 300, color: "#E0E0E0", maxWidth: "480px", margin: "0 auto 48px", lineHeight: 1.8 }}>
              Connect with our team and discover how Cadence Solutions can transform your business operations.
            </p>
            <a href="mailto:info@cadencesolutionsllc.com" className="btn-primary" style={{ fontSize: "12px" }}>Contact Us Today</a>
          </div>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer style={{
        padding: isMobile ? "40px 24px" : "40px 48px",
        borderTop: "1px solid #003366",
        background: "#00008B",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: isMobile ? "16px" : "0",
        textAlign: isMobile ? "center" : "left"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <img src={csLogo} alt="Cadence Solutions Logo" style={{ height: "32px", width: "auto" }} />
          <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <span className="cormorant" style={{ fontSize: "18px", fontWeight: 600, color: "#FFFFFF", letterSpacing: "1px" }}>Cadence</span>
            <span className="barlow" style={{ fontSize: "10px", letterSpacing: "3px", color: "#7ad6f4", textTransform: "uppercase" }}>Solutions LLC</span>
          </div>
        </div>
        <div className="barlow" style={{ fontSize: "12px", letterSpacing: "1px", color: "#8aaec8" }}>
          © 2025 Cadence Solutions LLC · cadencesolutionsllc.com
        </div>
        <a href="mailto:info@cadencesolutionsllc.com" className="barlow" style={{ fontSize: "12px", color: "#7ad6f4", textDecoration: "none", letterSpacing: "1px" }}>
          info@cadencesolutionsllc.com
        </a>
      </footer>
    </div>
  );
}
